import { Injectable } from '@angular/core';
import { EmployeeProfileService, SpAuthService, UserContextService } from '@core/services';
import { LoggerService, LogType } from '@core/services/utility-services/logger.service';
import { Indexable } from '@core/services/utils/service.utils';
import * as Sentry from '@sentry/angular';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

const denyUrls: (string | RegExp)[] = [
  /pendo\.io/i,
  /marketo\.net/i,
  /mktoresp\.com/i,
  /walkme\.com/i,
  /boldchat\.com/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
];

const ignoreErrors: (string | RegExp)[] = [
  'vwo_$', // VWO errors
  'Munchkin', // marketo
  '$ is not defined',
  'pendo',
  // Common 3rd party inlined-script errors.
  /cannot read properties of (undefined|null) \(reading '(style|prepend)'\)/i, // chrome
  'undefined is not an object (evaluating \'document.getElementsByClassName("bcFloat")[0].style\')', // safari
  // Assume we never write failing `document.getElementsByClassName` in Angular code
  'document.getElementsByClassName(...)[0] is undefined', // firefox
];

@Injectable({ providedIn: 'root' })
export class SentryService {
  constructor(
    private spAuthService: SpAuthService,
    private loggerService: LoggerService,
    private userContextService: UserContextService,
    private employeeProfileService: EmployeeProfileService
  ) {
    if (environment.thirdPartyConfigs.sentryEnabled) {
      this.configureSentry();
      this.addListeners();
    } else {
      console.warn('Sentry is disabled');
    }
  }

  private configureSentry() {
    // see https://docs.sentry.io/platforms/javascript/guides/angular/configuration/options/
    const options = {
      dsn: environment.sentryDsn,
      environment: environment.env,
      release: environment.version,
      denyUrls,
      ignoreErrors,
    };
    Sentry.init(options);
    Sentry.setTag('product', 'Ess');
  }

  private addListeners() {
    this.addAuthErrorListener();
    this.addLoggerListener();
    this.addUserListener();
  }

  /** Report when AuthService logs an error. */
  private addAuthErrorListener() {
    // No need to unsubscribe; this service never dies
    this.spAuthService.error$.subscribe(err => {
      if (err) {
        if (!this.isFiltered(err)) {
          Sentry.captureMessage(`errMsg: ${err.message} name: ${err.name}`, {
            level: 'error',
            tags: { location: 'auth' },
          });
        }
      }
    });
  }

  /** Report when ECommerce logger logs an error or warning. */
  private addLoggerListener() {
    this.loggerService.entries$
      .pipe(filter(e => e.type === 'error' || e.type === 'warn'))
      // No need to unsubscribe; this service never dies
      .subscribe(err => {
        Sentry.captureMessage(err.entry, {
          level: err.type === LogType.Error ? 'error' : 'warning',
          tags: { location: 'Logger Service' },
        });
      });
  }

  /** Set and update the Sentry user when the auth user and/or current user change. */
  private addUserListener() {
    combineLatest([
      this.userContextService.userContext$,
      this.employeeProfileService.employeeProfile$,
    ]).subscribe(([userContext, employeeProfile]) => {
      let sentryUser: Indexable<string>;

      if (userContext == null) {
        sentryUser = {
          username: 'Not signed in',
        };
      } else {
        sentryUser = {
          username: userContext.userName,
          companyName: userContext.companyName,
          employeeName: userContext.employeeName,
          partnerCode: userContext.partnerCode,
          email: employeeProfile.personal.email,
          bclCode: employeeProfile.personal.bclCode,
          employeeId: employeeProfile.personal.employeeId?.toString(),
        };
      }

      Sentry.setUser(sentryUser);
    });
  }

  private isFiltered(err: Error) {
    return (
      !!err.message &&
      (err.message.toLowerCase().trim() === 'login required' ||
        err.message.toLowerCase().trim() === 'invalid state' ||
        err.message.toLowerCase().trim() === 'multifactor authentication required')
    );
  }
}
